import moment from "moment";
import { ServicesFactoryInstance } from "../components/Controls/ServicesFactory";
import { ModalAlerteOptions } from "../components/modalComponents/modalAlerte/modalAlerte";
import { etatMapping, stateLabels, typeAbsenceIcon } from "../config/planningMensuel.config";
import { AbsencesPlanningMensuelDto } from "../dto/GenApi.iNotPaie.Dto/AbsencesPlanningMensuelDto";
import { CommandeChangementEtatAbsenceDto } from "../dto/GenApi.iNotPaie.Dto/CommandeChangementEtatAbsenceDto";
import { AbsenceStateEnum, IAbsenceStyle } from "../shared/interfaces/calendar.interface";
import { ISidePanelHistorique } from "../shared/interfaces/sidePanelHistorique.interface";
import { ColorServiceInstance } from "../shared/services/color.service";
import dateHelper from "./dateHelper";

/** Type pour un jour de calendrier (date utilisée par les grilles mensuel/équipe) */
export interface ICalendarDayLike {
    date: Date | string;
}

/** Type pour les flags de demi-journées */
export interface IHalfDayFlags {
    halfDayStart: boolean;
    halfDayEnd: boolean;
    isHalfDay: boolean;
}

/**
 * Récupère la durée de l'absence au format "XjYh" ou "Xj" ou "Yh"
 * @param nombreJours Nombre de jours
 * @param nombreHeures Nombre d'heures
 * @returns La durée de l'absence au format "XjYh" ou "Xj" ou "Yh"
 */
export function getDureeAbsence(nombreJours?: number, nombreHeures?: number): string {
    const units = [
        { value: nombreJours, label: "j" },
        { value: nombreHeures, label: "h" }
    ];

    let duree = "";
    for (const unit of units) {
        if (unit.value != null && unit.value > 0) {
            duree += `${unit.value}${unit.label}`;
        }
    }

    return duree;
}

/**
 * Libellé du type d'absence pour affichage (liste, drawer).
 * Ajoute la mention "(posé par le gestionnaire)" si besoin.
 */
export function getLibelleAbsence(libelleTypeAbsence: string | undefined, estPoseeParGestionnaire: boolean | undefined): string {
    if (estPoseeParGestionnaire)
        libelleTypeAbsence += " (posé par le gestionnaire)";
    return libelleTypeAbsence != undefined ? libelleTypeAbsence : "";
}

/**
 * Indique si l'absence débute avant la plage affichée (bord gauche "tronqué").
 * Utilisable en mensuel (plage = semaine) ou équipe (plage = mois).
 */
export function continuesLeft(
    startDate: Date | string | moment.Moment,
    rangeStartDate: Date | string | moment.Moment
): boolean {
    return moment(startDate).isBefore(moment(rangeStartDate).startOf("day"), "day");
}

/**
 * Indique si l'absence se termine après la plage affichée (bord droit "tronqué").
 */
export function continuesRight(
    endDate: Date | string | moment.Moment,
    rangeEndDate: Date | string | moment.Moment
): boolean {
    return moment(endDate).isAfter(moment(rangeEndDate).endOf("day"), "day");
}

/**
 * Index 1-based du jour dans la liste des jours de la semaine (pour les classes day-1 … day-7). Mode mensuel.
 */
export function getDayIndexInWeek(
    day: Date | string | moment.Moment,
    weekDays: ICalendarDayLike[],
    fallback: number
): number {
    const m = moment(day);
    const i = weekDays.findIndex(d => moment(d.date).isSame(m, "day"));
    return i >= 0 ? i + 1 : fallback;
}

/**
 * Index 0-based du jour dans la liste des jours du mois (grille équipe).
 */
export function getDayIndexInCalendar(
    day: Date | string | moment.Moment,
    calendarDays: ICalendarDayLike[],
    fallback: number
): number {
    const i = calendarDays.findIndex(d => moment(d.date).isSame(moment(day), "day"));
    return i >= 0 ? i : fallback;
}

/**
 * Calcule la position left (%) et width (%) de la barre d'absence pour le mode équipe.
 * Prend en compte les demi-journées (A = après-midi, M = matin) et des marges visuelles (gap).
 */
export function getBarPositionPercentages(
    totalDays: number,
    continuesLeft: boolean,
    continuesRight: boolean,
    startDayIndex: number,
    endDayIndex: number,
    halfDayStart: boolean,
    halfDayEnd: boolean
): { left: number; width: number } {
    // Si le nombre de jours est 0, on retourne 0% pour la position left et 100% pour la largeur
    if (totalDays === 0) return { left: 0, width: 100 };

    // On calcule l'offset de début et de fin
    const startOffset = startDayIndex + (halfDayStart ? 0.5 : 0);
    // On calcule l'offset de fin
    const endOffset = endDayIndex + 1 - (halfDayEnd ? 0.5 : 0);
    // On calcule la marge gauche
    const leftGap = continuesLeft ? 0 : 0.25;
    // On calcule la marge droite
    const rightGap = continuesRight ? 0 : 0.25;
    // On calcule la position left en %
    const leftPc = (startOffset / totalDays) * 100;
    // On calcule la largeur en %
    const widthPc = Math.max(0, ((endOffset - startOffset) / totalDays) * 100 - leftGap - rightGap);
    // On retourne la position left et la largeur en %
    return { left: leftPc + leftGap, width: widthPc };
}

/**
 * Libellé et couleur à afficher pour une absence (gère le cas "anonymisé" en équipe).
 */
export function getDisplayAbsence(
    libelleTypeAbsence: string | undefined,
    color: string | null | undefined,
    isAnonymized: boolean
): { libelle: string; libelleTooltip: string; color: string } {
    return {
        libelle: isAnonymized ? "Absence - Accès limité" : (libelleTypeAbsence ?? ""),
        libelleTooltip: isAnonymized ? "Absence" : (libelleTypeAbsence ?? ""),
        color: isAnonymized || color == null ? "#1C1D24" : color
    };
}

/**
 * Construit la liste des classes CSS pour la barre d'absence en mode mensuel.
 */
export function buildAbsenceClassesMensuel(
    startDayInWeek: number,
    durationInWeek: number,
    halfDayStart: boolean,
    halfDayEnd: boolean,
    continuesLeft: boolean,
    continuesRight: boolean,
    borderRadiusClass: string,
    absenceId: number
): string[] {
    const halfDayClasses: string[] = [];
    // On ajoute la classe half-day-start si l'absence commence en demi-journée
    if (halfDayStart) halfDayClasses.push("half-day-start");
    // On ajoute la classe half-day-end si l'absence finit en demi-journée
    if (halfDayEnd) halfDayClasses.push("half-day-end");
    // On retourne la liste des classes
    return [
        "absence-line",
        `day-${startDayInWeek}`,
        `duration-${durationInWeek}`,
        ...halfDayClasses,
        continuesRight ? "continues-right" : "",
        continuesLeft ? "continues-left" : "",
        borderRadiusClass,
        `absence-group-${absenceId}`
    ].filter(Boolean);
}

/** Classe CSS de bordure de la barre : single | start-only | end-only | middle. */
export function getBorderRadiusClass(continuesLeft: boolean, continuesRight: boolean): string {
    // Si l'absence continue à gauche et à droite, on retourne la classe middle
    if (continuesLeft && continuesRight) return "middle";
    // Si l'absence continue à gauche, on retourne la classe end-only
    if (continuesLeft) return "end-only";
    // Si l'absence continue à droite, on retourne la classe start-only
    if (continuesRight) return "start-only";
    // Sinon, on retourne la classe single
    return "single";
}

/**
 * Style visuel de la barre d'absence (couleur de fond, texte, bordure).
 * Validée = plein ; en attente = dashed + fond léger ; anonymisée (équipe) = gris.
 */
export function getAbsenceStyle(
    color: string,
    isValidated: boolean,
    isAnonymized?: boolean
): IAbsenceStyle {
    // Si l'absence est anonymisée, on retourne le style anonymisé
    if (isAnonymized) {
        return {
            backgroundColor: color,
            color: "#1C1D24",
            borderColor: color,
            borderStyle: "solid"
        };
    }
    // Si l'absence est validée, on retourne le style validé
    if (isValidated) {
        return {
            backgroundColor: color,
            color: "#FFFFFF",
            borderColor: color,
            borderStyle: "solid"
        };
    }
    // Sinon, on retourne le style en attente
    return {
        backgroundColor: ColorServiceInstance.hexToRgba(color, 0.08),
        color: color,
        borderColor: color,
        borderStyle: "dashed"
    };
}

/** Titre et icône de l'état de l'absence pour le tooltip. Si isAnonymized (équipe), retourne "Accès limité". */
export function getStateInfo(etat: number | null | undefined, isAnonymized?: boolean): { title: string; icon: string } {
    if (isAnonymized) return { title: "Accès limité", icon: "" };
    const state = etat != null && etatMapping[etat] ? etatMapping[etat] : AbsenceStateEnum.Validated;
    return stateLabels[state];
}

/** Classe d'icône du type d'absence (congé, RTT, etc.) ou cadenas si anonymisée. */
export function getTypeIcon(codeTypeAbsence: string | undefined, isAnonymized?: boolean): string {
    if (isAnonymized) return "ri-lock-line";
    return codeTypeAbsence && typeAbsenceIcon[codeTypeAbsence]
        ? typeAbsenceIcon[codeTypeAbsence]
        : typeAbsenceIcon["DEFAULT"];
}

/**
 * Indique si l'absence commence en demi-journée (après-midi) et/ou finit en demi-journée (matin).
 * "A" = après-midi, "M" = matin. Utilisé pour le positionnement visuel (mensuel).
 */
export function getHalfDayFlags(
    DebutMatinAprem: string | undefined,
    FinMatinAprem: string | undefined,
    isSameDay: boolean,
    continuesLeft: boolean,
    continuesRight: boolean
): IHalfDayFlags {
    let halfDayStart = false;
    let halfDayEnd = false;
    let isHalfDay = false;

    // Si l'absence est le même jour et ne continue pas à gauche et à droite, on vérifie si l'absence commence ou finit en demi-journée
    if (isSameDay && !continuesLeft && !continuesRight) {
        // Si l'absence commence et finit en demi-journée, on vérifie si l'absence commence ou finit en demi-journée
        if (DebutMatinAprem === "A" && FinMatinAprem === "A") {
            halfDayStart = true;
            isHalfDay = true;
            // Si l'absence finit en demi-journée, on vérifie si l'absence finit en demi-journée
        } else if (DebutMatinAprem === "M" && FinMatinAprem === "M") {
            halfDayEnd = true;
            isHalfDay = true;
        }
    } else {
        // Si l'absence ne continue pas à gauche, on vérifie si l'absence commence en demi-journée
        if (!continuesLeft && DebutMatinAprem === "A") halfDayStart = true;
        // Si l'absence ne continue pas à droite, on vérifie si l'absence finit en demi-journée
        if (!continuesRight && FinMatinAprem === "M") halfDayEnd = true;
    }
    return { halfDayStart, halfDayEnd, isHalfDay };
}

/** Indique si l'absence est dans un état "validé" (affichage plein, pas dashed). */
export function isValidatedPlanning(etat: number | null | undefined): boolean {
    return etat != null && etatMapping[etat] === AbsenceStateEnum.Validated;
}

/** Détruit le DOM du popper (menu contextuel) pour éviter résidus. */
export function cleanPopperElm(ref: any): void {
    if (ref?.popperElm) {
        ref.popperElm.remove();
        ref.popperElm = null;
    }
}

/** Nettoyage tooltip avec fallback retardé si le popper n'existe pas encore. */
export function cleanTooltipPopper(ref: any): void {
    if (ref?.popperElm) {
        ref.popperElm.remove();
        ref.popperElm = null;
    } else if (ref) {
        setTimeout(() => {
            if (ref.popperElm) {
                ref.popperElm.remove();
                ref.popperElm = null;
            }
        }, 150);
    }
}

/** Options de la modale de confirmation d'annulation d'une demande d'absence. */
export function getCancelAbsenceModalOptions(callBackYes: () => void): ModalAlerteOptions {
    return new ModalAlerteOptions({
        title: "Annulation demande d'absence",
        message: "Êtes-vous sûr de vouloir annuler votre demande d'absence ?",
        yesText: "Annuler ma demande",
        noText: "Annuler",
        callBackYes
    });
}

/** Options de la modale de confirmation de suppression de la demande d'annulation. */
export function getCancelCancellationModalOptions(
    callBackYes: () => void,
    callBackNo?: () => void
): ModalAlerteOptions {
    return new ModalAlerteOptions({
        title: "Suppression demande d'annulation",
        message: "Êtes-vous sûr de vouloir supprimer votre demande d'annulation ?",
        yesText: "Supprimer l'annulation",
        noText: "Annuler",
        callBackYes,
        callBackNo: callBackNo ?? (() => { })
    });
}

/** Appel API changement d'état (annuler / annuler demande d'annulation) puis callbacks succès/erreur. */
export function executeChangementEtatAbsence(
    commande: CommandeChangementEtatAbsenceDto,
    onSuccess: () => void,
    onError: () => void
): void {
    ServicesFactoryInstance.AbsencesService.changerEtatAbsenceV2(commande, true, onSuccess, onError);
}

/** Formate une absence pour l'affichage dans le panneau latéral (id, type, statut, dates). */
export function convertAbsenceToSidePanelHistorique(
    absence: AbsencesPlanningMensuelDto,
    typeLabel: string
): ISidePanelHistorique {
    return {
        id: absence.Id,
        type: typeLabel,
        statut: absence.Etat,
        dateDebut: dateHelper.formatDate(absence.DateDebut),
        dateFin: dateHelper.formatDate(absence.DateFin)
    };
}



// WEBPACK FOOTER //
// ./src/Helper/absencePlanningHelper.ts